// index.js
import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import BarLoader from "react-spinners/BarLoader";
import { css } from "@emotion/react";

import { UserProvider } from "../contexts/UserContext";
import { NotificationsProvider } from "../contexts/NotificationsContext";

import "./index.css";
import "./chart.css";
import "./step.css";

import Footer from "./layouts/Footer";
import Header from "./layouts/nav/Header";

import Login from "../screens/Login";
import GeneralCustomers from "../screens/GeneralCustomers";
import CustomerDetails from "../screens/CustomerDetails";
import Users from "../screens/Users";
import UserDetails from "../screens/UserDetails";
import Register from "../screens/Register";
import Campaigns from "../screens/Campaigns";
import CampaignDetails from "../screens/CampaignDetails";
import WhatsappTemplates from "../screens/WhatsappTemplates";
import WhatsappAgents from "../screens/WhatsappAgents";
import Account from "../jsx/components/WhatsAppChat/Account";
import WhatsAppChat from "../jsx/components/WhatsAppChat/WhatsAppChat";
import { parseCookies } from "nookies";
import Calendar from "./components/FullCalendar";
import AutomationsList from "../screens/Automations/AutomationsList";
import AutomationDetail from "../screens/Automations/AutomationDetail";
import AutomationRuleForm from "../screens/Automations/AutomationRuleForm";
import AutomationForm from "../screens/Automations/AutomationForm";
import MobileHeader from "./layouts/nav/MobileHeader";
import ContactBoard from "../jsx/components/ContactsBoard/";
import BusinessSettings from "../screens/Business/BusinessSettings";
import BottomNavigation from "./layouts/nav/BottomNavigation";
import Dashboard from "./components/Dashboard/Dashboard";
import AgentWizard from "./components/Agents/AgentWizard";
import CustomerList from "../screens/Customers/CustomerList";
import TagsManager from "../screens/Tags/TagsManager";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import UserList from "../screens/Users/UserList";
import WelcomeScreen from "../screens/Welcome/WelcomeScreen";
import SmartDashboardRoute from "./components/Auth/SmartDashboardRoute";
import { MoonLoader } from "react-spinners";

const Main = ({ isLoading, setIsLoading }) => {
  const { token: _token } = parseCookies();
  const [hasChatWrapper, setHasChatWrapper] = useState(false);
  const [logged, setLogged] = useState(false);
  const userName = useRef();

  useEffect(() => {
    setLogged(!!_token);
    if(!_token){
      setIsLoading(false)
    }
  }, [_token]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const exists = document.getElementById("chat-wrapper");
      setHasChatWrapper(!!exists);
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Checagem inicial
    setHasChatWrapper(!!document.getElementById("chat-wrapper"));

    return () => observer.disconnect();
  }, []);

  return (
    <Router>
      <UserProvider userName={userName} setIsLoading={setIsLoading}>
        <NotificationsProvider>
          {logged ? (
            window.innerWidth <= 1024 ? (
              <MobileHeader />
            ) : (
              <Header />
            )
          ) : null}

          {isLoading ? (
            <div className="loading-overlay">
              <MoonLoader color="var(--primary)" size={40} />
            </div>
          ) : (
            <Routes>
              {/* ========== ROTAS PÚBLICAS ========== */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* ========== DASHBOARD (ROTA BÁSICA) ========== */}

              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <SmartDashboardRoute />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/welcome"
                element={
                  <ProtectedRoute>
                    <WelcomeScreen />
                  </ProtectedRoute>
                }
              />

              {/* ========== CHAT E COMUNICAÇÃO ========== */}
              <Route
                path="/chat"
                element={
                  <ProtectedRoute requiredModule="Chat" requiredAction="read">
                    <WhatsAppChat />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/whatsapp"
                element={
                  <ProtectedRoute
                    requiredModule="WhatsApp"
                    requiredAction="read"
                    requiredLevel={2}
                  >
                    <Account />
                  </ProtectedRoute>
                }
              />

              {/* ========== AGENDA ========== */}

              <Route
                path="/calendar"
                element={
                  <ProtectedRoute requiredModule="Agenda" requiredAction="read">
                    <Calendar />
                  </ProtectedRoute>
                }
              />

              {/* ========== AGENTES IA ========== */}
              <Route
                path="/agents"
                element={
                  <ProtectedRoute
                    requiredModule="Meus Agentes"
                    requiredAction="read"
                  >
                    <WhatsappAgents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agents/create"
                element={
                  <ProtectedRoute
                    requiredModule="Criar Agente"
                    requiredAction="create"
                  >
                    <AgentWizard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agents/:id"
                element={
                  <ProtectedRoute
                    requiredModule="Meus Agentes"
                    requiredAction="update"
                  >
                    <AgentWizard />
                  </ProtectedRoute>
                }
              />

              {/* ========== CLIENTES ========== */}
              <Route
                path="/customers"
                element={
                  <ProtectedRoute
                    requiredModule="Base de Clientes"
                    requiredAction="read"
                  >
                    <CustomerList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/general-customers"
                element={
                  <ProtectedRoute
                    requiredModule="Base de Clientes"
                    requiredAction="read"
                  >
                    <GeneralCustomers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customer-details/:id"
                element={
                  <ProtectedRoute
                    requiredModule="Base de Clientes"
                    requiredAction="read"
                  >
                    <CustomerDetails />
                  </ProtectedRoute>
                }
              />

              {/* ========== TEMPLATES ========== */}
              <Route
                path="/templates"
                element={
                  <ProtectedRoute
                    requiredModule="Templates"
                    requiredAction="read"
                  >
                    <WhatsappTemplates />
                  </ProtectedRoute>
                }
              />

              {/* ========== CAMPANHAS/PUBLICAÇÕES ========== */}
              <Route
                path="/campaigns"
                element={
                  <ProtectedRoute
                    requiredModule="Publicações"
                    requiredAction="read"
                  >
                    <Campaigns />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/detalhes-campanha"
                element={
                  <ProtectedRoute
                    requiredModule="Publicações"
                    requiredAction="read"
                  >
                    <CampaignDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/detalhes-campanha/:id"
                element={
                  <ProtectedRoute
                    requiredModule="Publicações"
                    requiredAction="read"
                  >
                    <CampaignDetails />
                  </ProtectedRoute>
                }
              />

              {/* ========== AUTOMAÇÕES ========== */}
              <Route
                path="/automations"
                element={
                  <ProtectedRoute
                    requiredModule="Automações"
                    requiredAction="read"
                  >
                    <AutomationsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/automations/create"
                element={
                  <ProtectedRoute
                    requiredModule="Automações"
                    requiredAction="create"
                  >
                    <AutomationForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/automations/edit/:id"
                element={
                  <ProtectedRoute
                    requiredModule="Automações"
                    requiredAction="update"
                  >
                    <AutomationRuleForm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/automations/:id"
                element={
                  <ProtectedRoute
                    requiredModule="Automações"
                    requiredAction="read"
                  >
                    <AutomationDetail />
                  </ProtectedRoute>
                }
              />

              {/* ========== CRM ========== */}
              <Route
                path="/crm"
                element={
                  <ProtectedRoute
                    requiredModule="Pipeline CRM"
                    requiredAction="read"
                  >
                    <ContactBoard />
                  </ProtectedRoute>
                }
              />

              {/* ========== TAGS & CATEGORIAS ========== */}
              <Route
                path="/tags"
                element={
                  <ProtectedRoute
                    requiredModule="Tags & Categorias"
                    requiredAction="read"
                  >
                    <TagsManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tags/:id"
                element={
                  <ProtectedRoute
                    requiredModule="Tags & Categorias"
                    requiredAction="update"
                  >
                    <TagsManager />
                  </ProtectedRoute>
                }
              />

              {/* ========== USUÁRIOS (ROTAS CRÍTICAS) ========== */}
              <Route
                path="/users"
                element={
                  <ProtectedRoute
                    requireSuperuser={true}
                    requiredLevel={3}
                    requiredPermission={(user) => user.can_create_users}
                    showLoadingForPermissions={true}
                  >
                    <UserList />
                  </ProtectedRoute>
                }
              />

              {/* ========== PERFIL DO USUÁRIO ========== */}
              <Route
                path="/user-details"
                element={
                  <ProtectedRoute
                    requiredModule="Meu Perfil"
                    requiredAction="read"
                  >
                    <UserDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-details/:id"
                element={
                  <ProtectedRoute
                    requiredPermission={(user) =>
                      user.can_create_users || user.superuser_level <= 2
                    }
                  >
                    <UserDetails />
                  </ProtectedRoute>
                }
              />

              {/* ========== CONFIGURAÇÕES EMPRESARIAIS ========== */}
              <Route
                path="/business-settings"
                element={
                  <ProtectedRoute
                    requiredModule="Minha Empresa"
                    requiredAction="update"
                    requiredLevel={2}
                    showLoadingForPermissions={true}
                  >
                    <BusinessSettings />
                  </ProtectedRoute>
                }
              />

              {/* ========== PLAYGROUND (NOVO MÓDULO) ========== */}
              <Route
                path="/playground"
                element={
                  <ProtectedRoute
                    requiredModule="Playground"
                    requiredAction="read"
                  >
                    <div className="container mt-5">
                      <h2>Playground</h2>
                      <p>Área de testes e experimentação</p>
                    </div>
                  </ProtectedRoute>
                }
              />

              {/* ========== ESTATÍSTICAS ========== */}
              <Route
                path="/statistics"
                element={
                  <ProtectedRoute
                    requiredModule="Estatísticas"
                    requiredAction="read"
                    requiredLevel={3}
                  >
                    <div className="container mt-5">
                      <h2>Estatísticas</h2>
                      <p>Relatórios e métricas do sistema</p>
                    </div>
                  </ProtectedRoute>
                }
              />

              {/* ========== INTEGRAÇÕES ========== */}
              <Route
                path="/integrations"
                element={
                  <ProtectedRoute
                    requiredModule="Integrações"
                    requiredAction="read"
                    requiredLevel={2}
                  >
                    <div className="container mt-5">
                      <h2>Integrações</h2>
                      <p>Configurações de integrações externas</p>
                    </div>
                  </ProtectedRoute>
                }
              />

              {/* ========== PERSONALIZAÇÕES ========== */}
              <Route
                path="/customizations"
                element={
                  <ProtectedRoute
                    requiredModule="Personalizações"
                    requiredAction="read"
                    requiredLevel={2}
                  >
                    <div className="container mt-5">
                      <h2>Personalizações</h2>
                      <p>Configurações visuais e comportamentais</p>
                    </div>
                  </ProtectedRoute>
                }
              />

              {/* ========== SUPORTE ========== */}
              <Route
                path="/support"
                element={
                  <ProtectedRoute
                    requiredModule="Suporte"
                    requiredAction="read"
                  >
                    <div className="container mt-5">
                      <h2>Suporte</h2>
                      <p>Central de ajuda e suporte técnico</p>
                    </div>
                  </ProtectedRoute>
                }
              />

              {/* ========== PÁGINA 404 ========== */}
              <Route
                path="*"
                element={
                  <ProtectedRoute>
                    <div className="container mt-5">
                      <div className="text-center">
                        <h2>Página não encontrada</h2>
                        <p>A página que você está procurando não existe.</p>
                        <a href="/" className="btn btn-primary">
                          Voltar ao Dashboard
                        </a>
                      </div>
                    </div>
                  </ProtectedRoute>
                }
              />
            </Routes>
          )}

          {window.innerWidth < 768 && _token ? <BottomNavigation /> : null}
        </NotificationsProvider>
      </UserProvider>
    </Router>
  );
};

export default Main;
